import React, { useCallback, useMemo, useState } from 'react';
import FullList from '../../../components/FullList';
import { billlingPricesColumns } from '../enums/columns';
import { useBillingPrices, useBillingPricesExport } from '../hooks/api/billingPricesApi';
import useAppContext from '../../../hooks/context/useAppContext';
import DataTableBodyCellGraph from '../../../components/DataTable/DataTableBody/DataTableBodyCell/DataTableBodyCellGraph';
import { withStyles } from '@material-ui/core/styles';
import { LinearProgress } from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import { getMonthName } from '../../../utils/date';
import { mapEnumsToFilterList, remapObjectArrayToFilterList } from '../../../utils/objectArray';
import { useOrganizationEnum } from '../../../hooks/api/organizationEnumApi';
import DataTableBodyCellDecimal from '../../../components/DataTable/DataTableBody/DataTableBodyCell/DataTableBodyCellDecimal';
import FileExport from '../../../components/FileExport/FileExport';

const columns = [
  billlingPricesColumns.state,
  billlingPricesColumns.contractNumber,
  billlingPricesColumns.organization,
  billlingPricesColumns.frameContract,
  billlingPricesColumns.currency,
  billlingPricesColumns.invoicingCurrency,
  billlingPricesColumns.periodMonth,
  billlingPricesColumns.period,
  billlingPricesColumns.commodity,
  billlingPricesColumns.product,
  billlingPricesColumns.pnMethod,
  billlingPricesColumns.quantity,
  billlingPricesColumns.percentagePurchasedQuantity,
  billlingPricesColumns.purchasedQuantity,
  billlingPricesColumns.remainingPurchaseQuantity,
  billlingPricesColumns.tariff1,
  billlingPricesColumns.tariff2,
  billlingPricesColumns.tariff3,
  billlingPricesColumns.tariff4,
  billlingPricesColumns.tariff5,
  billlingPricesColumns.tariff6,
];

const getNameOfMonth = (intl, month) => {
  return getMonthName(intl, new Date(2000, month - 1, 1));
};

const BillingPricesList = () => {
  const { getAllBillingPrices, data, loading, error } = useBillingPrices();
  const { getAllBillingPricesExport, data: exportData, error: exportError, loading: exportLoading } = useBillingPricesExport();
  const { getOrganizationEnum } = useOrganizationEnum();
  const { user, enums } = useAppContext();
  const [errorMessage, setErrorMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState({});
  const intl = useIntl();

  const initialValues = {
    page: 0,
    rowsPerPage: user?.initialRowsPerPage,
    order: 'asc',
    orderBy: 'organizationName',
    fulltext: '',
    filters: {
      organizationId: user?.customerIds,
      state: [0],
    },
    filterOptions: {
      state: [{ id: 0 }, { id: 1 }],
    },
  };

  const toolbarComponents = [
    <FileExport
      getAllEntityExport={getAllBillingPricesExport}
      tooltip={<FormattedMessage id={'app.dataTable.toolbarIconTooltip.export'} />}
      fileName={'EEM_fakturacni_ceny_export.xlsx'}
      data={exportData}
      searchQuery={searchQuery}
      exportError={exportError}
      setErrorMessage={setErrorMessage}
      key={'toolbar-file-export'}
    />,
  ];

  const processData = (dataToProcess) => {
    const StyledProgressBar = withStyles({
      colorPrimary: {
        backgroundColor: `#b8b3ad`,
      },
      barColorPrimary: {
        backgroundColor: `#f24f00`,
      },
    })(LinearProgress);
    return {
      data: dataToProcess.data.map((item) => {
        const totalPercent = item?.percentagePurchasedQuantity || 0;
        const output = {
          state: item?.contract?.state ? (
            <FormattedMessage id={'app.pages.customerListPage.state.active'} />
          ) : (
            <FormattedMessage id={'app.pages.customerListPage.state.inactive'} />
          ),
          contractNumber: item?.contract?.idExternal,
          organizationName: item?.contract?.customer?.name,
          frameContract: item?.contract?.frameContract,
          currency: item?.contract?.currency.id,
          invoicingCurrency: item?.contract?.invoicingCurrency.id,
          month: getNameOfMonth(intl, item?.contractQuantity?.month),
          period: item?.contract?.contractPeriod,
          commodity: item?.contract?.commodity.name,
          isMonthlyPurchaseAvailable: item?.contract?.isMonthlyPurchaseAvailable ? (
            <FormattedMessage id={'app.billingprices.product.intrayear'} />
          ) : (
            <FormattedMessage id={'app.billingprices.product.year'} />
          ),
          purchaseMethod: item?.contract?.purchaseMethod?.name,
          quantity: <DataTableBodyCellDecimal value={item?.contractQuantity?.quantity} />,
          purchasedQuantity: <DataTableBodyCellDecimal value={item?.purchasedQuantity} />,
          remainingPurchaseQuantity: <DataTableBodyCellDecimal value={item?.remainingPurchaseQuantity} />,
          percentagePurchasedQuantity: (
            <DataTableBodyCellGraph
              graph={<StyledProgressBar variant="determinate" value={totalPercent} />}
              text={
                <>
                  <DataTableBodyCellDecimal value={totalPercent} />%
                </>
              }
            />
          ),
          tariff1: null,
          tariff2: null,
          tariff3: null,
          tariff4: null,
          tariff5: null,
          tariff6: null,
        };

        item.tarifPrices.forEach((tariff) => {
          output[`tariff${tariff.idTarif}`] = <DataTableBodyCellDecimal value={tariff?.tariffPriceWeightedAvg} />;
        });

        return output;
      }),
      totalCount: dataToProcess.totalCount,
    };
  };

  const columnFilters = useMemo(
    () => ({
      [billlingPricesColumns.organization.id]: async () => getOrganizationEnum(),
      [billlingPricesColumns.pnMethod.id]: () => mapEnumsToFilterList(enums?.['purchaseMethods']),
      [billlingPricesColumns.currency.id]: () => mapEnumsToFilterList(enums?.['currencies']),
      [billlingPricesColumns.invoicingCurrency.id]: () => mapEnumsToFilterList(enums?.['currencies']),
      [billlingPricesColumns.commodity.id]: () => mapEnumsToFilterList(enums?.['commodities']),
      [billlingPricesColumns.period.id]: () => remapObjectArrayToFilterList(enums?.['years'], 'year', 'year'),
      [billlingPricesColumns.product.id]: () => [
        {
          id: true,
          label: intl.formatMessage({ id: 'app.billingprices.product.intrayear' }),
        },
        {
          id: false,
          label: intl.formatMessage({ id: 'app.billingprices.product.year' }),
        },
      ],
      [billlingPricesColumns.periodMonth.id]: () => {
        const output = [];
        for (let i = 0; i < 12; i++) {
          output.push({ id: i + 1, label: getNameOfMonth(intl, i + 1) });
        }
        return output;
      },
      [billlingPricesColumns.percentagePurchasedQuantity.id]: () => [
        {
          id: 0,
          label: '0%',
        },
        {
          id: 100,
          label: '100%',
        },
      ],
      [billlingPricesColumns.state.id]: () => [
        {
          id: 1,
          label: intl.formatMessage({ id: 'app.pages.customerListPage.state.active' }),
        },
        {
          id: 0,
          label: intl.formatMessage({ id: 'app.pages.customerListPage.state.inactive' }),
        },
      ],
    }),
    [getOrganizationEnum, enums, intl],
  );

  const onFilterOpen = useCallback(
    async (column) => {
      return columnFilters?.[column] && columnFilters[column]();
    },
    [columnFilters],
  );

  return (
    <>
      <FullList
        getData={getAllBillingPrices}
        loading={loading || exportLoading}
        error={error}
        data={data}
        processData={processData}
        columns={columns}
        toolbarComponents={toolbarComponents}
        initialValues={initialValues}
        showToolbarIcons
        onFilterOpen={onFilterOpen}
        setSearchQuery={setSearchQuery}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
      />
    </>
  );
};

BillingPricesList.propTypes = {};

export default BillingPricesList;
